import useSegmentTags from './useSegmentTags';
import useReportSegmentTags from './useReportSegmentTags';
import useDataItemSegmentTags from './useDataItemSegmentTags';
import useDataItemTags from './useDataItemTags';

/**
 * Store-aware tagging router for unified-search result cards. Every store keys tags in its
 * own table, so the card picks a strategy by docSourceType:
 *
 *   CORPUS    → useSegmentTags          (entity_tags SEGMENT, per-user votes)
 *   REPORT    → useReportSegmentTags    (entity_tags.report_segment_id, per-user votes)
 *   MY_DATA   → useDataItemSegmentTags  (data_item_segment_tag_link, toggle)
 *   HIGHLIGHT → useDataItemTags         (data_item_tag_link on the bookmark item, toggle)
 *
 * All four hooks are called unconditionally (Rules of Hooks); the non-selected ones receive
 * null ids and no-op. Returns the useSegmentTags contract
 * ({tags, createTag, voteTag, isPosting, loadingTagOps}) so SegmentContent/TagList are
 * unchanged.
 */
export default function useSearchResultTags({
    docSourceType,
    // corpus
    docId,
    segmentId,
    segmentIndex,
    segmentTags,
    offset,
    // products (report paragraph)
    reportSegmentId,
    // my data (sentence segment)
    dataItemSegmentId,
    // highlights (bookmark data item)
    highlightDataItemId,
}) {
    const store = docSourceType || 'CORPUS';

    const corpus = useSegmentTags({
        docId: store === 'CORPUS' ? docId : null,
        segmentId: store === 'CORPUS' ? segmentId : null,
        segmentIndex: store === 'CORPUS' ? segmentIndex : null,
        segmentTags: store === 'CORPUS' ? segmentTags : undefined,
        offset,
    });

    const report = useReportSegmentTags({
        reportSegmentId: store === 'REPORT' ? reportSegmentId : null,
        initialTags: store === 'REPORT' ? segmentTags : undefined,
    });

    const myData = useDataItemSegmentTags({
        dataItemSegmentId: store === 'MY_DATA' ? dataItemSegmentId : null,
        initialTags: store === 'MY_DATA' ? segmentTags : undefined,
    });

    const highlight = useDataItemTags({
        dataItemId: store === 'HIGHLIGHT' ? highlightDataItemId : null,
        initialTags: store === 'HIGHLIGHT' ? segmentTags : undefined,
    });

    if (store === 'REPORT') return report;
    if (store === 'MY_DATA') return myData;
    if (store === 'HIGHLIGHT') return highlight;
    return corpus;
}
